<script setup lang="ts">
    import BackButton from './BackButton.ce.vue';

    const emit = defineEmits<{(event: 'backToMain')}>();

    const { t } = useI18n();

    const { venue } = storeToRefs(useWidgetVenueStore());
</script>

<template>
    <div class="allstarts-login">
        <div class="allstarts-login__back">
            <BackButton @click="emit('backToMain')" />
        </div>
        <div class="allstarts-login__form">
            <!--
                `_top` so the POST leaves the widget iframe: the response is the AllStars
                login page, which must load in the customer's tab (its session cookies are
                blocked in a cross-origin frame, and it would render inside our clipped
                iframe). Top-level navigation is allowed here - the frame is not sandboxed
                and the submit is a user gesture. On the local dev page `_top` == self.
            -->
            <form
                id="loginForm"
                :action="venue?.allStartsQuoteUrl"
                method="post"
                target="_top"
            >
                <div class="allstarts-login__form__content">
                    <h2>
                        {{ t('widget.login.title') }}
                    </h2>
                    <span>
                        {{ t('widget.login.subtitle') }}
                    </span>
                    <input
                        :placeholder="`${t('widget.form.email')}*`"
                        required
                        type="text"
                        name="username"
                        value=""
                    >
                    <input
                        :placeholder="`${t('widget.form.password')}*`"
                        required
                        type="password"
                        name="password"
                        value=""
                    >
                    <a
                        :href="venue?.allStartsQuoteUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="allstarts-login__forgot"
                    >
                        {{ t('widget.form.forgot-password') }}
                    </a>
                    <button
                        id="loginx"
                        type="submit"
                        name="submit"
                        value="login"
                        class="btn btn--primary"
                    >
                        <span>
                            {{ t('widget.buttons.login') }}
                        </span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.allstarts-login {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: var(--spacing-03);
    block-size: 100%;
    inline-size: 100%;

    @include min-width(md) {
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    @include min-width(lg) {
        align-items: flex-start;
        block-size: auto;
    }

    &__form {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--color-pure-white);
        border-radius: var(--border-radius-md);
        background: var(--background-primary);
        gap: var(--spacing-03);
        padding-block: var(--spacing-05);
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            inline-size: 100%;
            margin-block-start: calc(-1 * 50px);
            margin-inline: auto;
            max-inline-size: 548px;
            padding-block: var(--spacing-06) var(--spacing-04);
            padding-inline: var(--spacing-09);
        }

        @include min-width(lg) {
            margin-block: 0 var(--spacing-05);
        }

        &__content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--spacing-02);

            @include min-width(md) {
                gap: var(--spacing-03);
            }

            h2, > span {
                text-align: center;
            }

            > span {
                display: block;
                margin-block-end: var(--spacing-04);
            }

            input {
                box-sizing: border-box;
                padding: var(--spacing-03);
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-xs);
                background-color: var(--background-form-input);
                block-size: var(--size-11);
                font-size: var(--font-medium);
                inline-size: 100%;
                line-height: 1;

                &::placeholder {
                    color: var(--text-placeholder);
                }

                &:hover,
                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }

                @include min-width(md) {
                    block-size: var(--size-12);
                }

                @include min-width(lg) {
                    block-size: var(--size-13);
                }
            }
        }
    }

    &__forgot {
        color: var(--text-secondary);
        font-size: var(--font-small);
        margin-block-end: var(--spacing-04);
        text-decoration: underline;
    }
}
</style>
